import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from '@dr.pogodin/react-helmet'
import Swal from 'sweetalert2'
import { useDispatch } from 'react-redux'
import { useAuth } from '../../hooks/useAuth'
import { authService } from '../../services/authService'
import { setCredentials } from '../../store/slices/authSlice'
import logo from '../../assets/images/logo.png'
import Chrome from '../../assets/images/adminLogin/chrome.png'
import Firefox from '../../assets/images/adminLogin/firefox.png'
import Edge from '../../assets/images/adminLogin/ie.png'
import FooterSection from '@/components/Footer/LoginFooter'

// Helper function to check if user is owner by user ID
const isOwnerByUserId = (userId) => {
  if (!userId) return false
  const ownerUserIds = ['1', 1, 'owner', 'diamond_owner', 'diamond99_owner']
  const userIdStr = String(userId).toLowerCase()
  return ownerUserIds.some(id => userIdStr === String(id).toLowerCase() || userIdStr.includes('owner'))
}

function Login() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loginAsync, isLoggingIn, logout: authLogout } = useAuth()
  const confirmationPendingRef = useRef(false)

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })

  const [errors, setErrors] = useState({
    username: '',
    password: '',
    general: ''
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [loginData, setLoginData] = useState(null)

  console.log("landed on")

  // Auto-clear general error after 3 seconds
  useEffect(() => {
    if (errors.general) {
      const timer = setTimeout(() => {
        setErrors(prev => ({ ...prev, general: '' }))
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [errors.general])

  // Toast configuration
  // const Toast = Swal.mixin({
  //   toast: true,
  //   position: 'top-end',
  //   showConfirmButton: false,
  //   timer: 3000,
  //   timerProgressBar: false,
  //   didOpen: (toast) => {
  //     toast.onmouseenter = Swal.stopTimer
  //     toast.onmouseleave = Swal.resumeTimer
  //   }
  // })

  const validateField = (name, value) => {
    let error = ''

    if (name === 'username') {
      if (!value || value.trim() === '') {
        error = 'The username field is required'
      }
    } else if (name === 'password') {
      if (!value || value.trim() === '') {
        error = 'The password field is required'
      }
    }

    return error
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData({
      ...formData,
      [name]: value
    })

    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleSubmit = async () => {
 setShowConfirmation(true)


  }

  const handleConfirm = async () => {





    const usernameError = validateField('username', formData.username)
    const passwordError = validateField('password', formData.password)

    setErrors({
      username: usernameError,
      password: passwordError,
      general: ''
    })

    if (!usernameError && !passwordError) {
      try {
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)

        const credentials = {
          password: formData.password
        }

        if (isEmail) {
          credentials.email = formData.username
        } else {
          credentials.username = formData.username
        }

        // Prevent any premature profile fetch/navigation
        localStorage.setItem('loginConfirmationPending', 'true')

        const response = await loginAsync(credentials)

        // Decode and log token expiration info
        if (response?.token) {
          try {
            const payload = JSON.parse(atob(response.token.split('.')[1]))
            const issuedAt = payload.iat ? new Date(payload.iat * 1000) : null
            const expiresAt = payload.exp ? new Date(payload.exp * 1000) : null
            const now = new Date()

            console.log('=== TOKEN INFO ===')
            console.log('Token:', response.token)
            console.log('Token Payload:', payload)
            if (issuedAt) console.log('Issued At:', issuedAt.toLocaleString())
            if (expiresAt) {
              console.log('Expires At:', expiresAt.toLocaleString())
              const remainingMs = expiresAt - now
              const remainingMinutes = Math.floor(remainingMs / 60000)
              const remainingHours = Math.floor(remainingMinutes / 60)
              const remainingDays = Math.floor(remainingHours / 24)
              console.log(`Time Remaining: ${remainingDays}d ${remainingHours % 24}h ${remainingMinutes % 60}m`)
            } else {
              console.log('No expiration set on token')
            }
            console.log('==================')
          } catch (tokenErr) {
            console.warn('Could not decode token:', tokenErr)
          }
        }

        // Check for 2FA Requirement FIRST - before checking success
        if (response?.require2FA === true) {
            localStorage.removeItem('loginConfirmationPending'); // Clear pending flag as we are moving to next step
            // Get the ID from response - could be staffId, ownerId, or userId
            const id = response.staffId || response.ownerId || response.userId || response.id;
            // Determine if it's an owner based on ownerId presence or check credentials
            const isOwner = !!response.ownerId || isOwnerByUserId(id);
            navigate('/security-verification', { state: { staffId: id, isOwner } });
            return;
        }

        const isSuccess = response?.success === true || (response?.token && !response?.error)

        if (isSuccess) {
          confirmationPendingRef.current = true

          try {
            const profileResponse = await authService.getProfile()
            console.log('Profile response:', profileResponse)

            // Determine user data based on role (similar to App.jsx/useAuth logic)
            const activeRole = localStorage.getItem('activeRole')
            const userData = activeRole === 'owner'
              ? profileResponse.data
              : profileResponse.data?.user

            if (userData) {
              dispatch(setCredentials({
                token: response.token || localStorage.getItem('token'),
                user: userData,
                role: activeRole
              }))
            }

            const loginDataToStore = {
              response,
              credentials
            }

            setLoginData(loginDataToStore)

            // Clear confirmation pending flag as we have completed the flow
            localStorage.removeItem('loginConfirmationPending')

            setShowConfirmation(true)
          } catch (profileError) {
             console.error("Error fetching profile during login confirmation:", profileError)
             // Fallback: still show confirmation or let the app redirect,
             // but Header/App will try to fetch profile again.
             const loginDataToStore = {
              response,
              credentials
            }
            setLoginData(loginDataToStore)
            setShowConfirmation(true)
          }
        } else {
          localStorage.removeItem('loginConfirmationPending')

          const msg = 'Incorrect Username or Password.'
          setErrors({ ...errors, general: msg })
          // Toast.fire({
          //   icon: 'error',
          //   title: msg
          // })
        }
      } catch (error) {
        localStorage.removeItem('loginConfirmationPending')
        setShowConfirmation(false)
        const msg = error?.response?.data?.message || 'Incorrect Username or Password.'
        setErrors({ ...errors, general: msg })
        // Toast.fire({
        //   icon: 'error',
        //   title: msg
        // })
      }
    }
  }

  const handleExit = () => {
    confirmationPendingRef.current = false
    setShowConfirmation(false)
    localStorage.removeItem('loginConfirmationPending')

    // Logout user if they cancel confirmation
    authLogout()

    setFormData({ username: '', password: '' })
    setErrors({})
    setLoginData(null)
  }

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=1020" />
      </Helmet>

      {/* Age Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-opacity-90 flex items-start justify-center z-50 pt-2" style={{ zIndex: 9999 }}>
          <div className="bg-[#333d79ff] rounded-sm w-full max-w-[21rem] mx-1 shadow-xl">
            <div className="bg-[#333d79ff] px-4 py-2 border-b border-white border-opacity-90">
              <h2 className="text-white text-[20px] font-medium">Confirm</h2>
            </div>
            <div className="p-4">
              <p className="text-white text-[14px]">
                Underage gambling is prohibited. Please confirm if you are 18 years old and above as of today.
              </p>
            </div>
            <div className="flex justify-end space-x-1 p-4 pt-0">
              <button
                onClick={handleExit}
                className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors text-sm font-medium"
              >
                Exit
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-green-500 text-white hover:bg-green-600 transition-colors text-sm font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='min-h-screen bg-[#333d79] flex items-center justify-center px-12'>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
        </div>

        <div className='relative w-full max-w-[400px] mx-auto'>
          <div className='bg-[#1f2549] rounded-sm shadow-xl px-12 pt-12 pb-16'>
            <div className='text-center'>
              <div className="text-center mb-3 px-4">
                <img
                  src={logo}
                  alt="DIAMOND EXCH 99"
                  className="w-[151px] h-[100px] mx-auto object-contain"
                />
              </div>
            </div>

            <div className='space-y-4' onSubmit={handleSubmit} autoComplete="off">
              {errors.general && (
                <div className="bg-[#f43] text-white px-3 py-2 text-center text-sm">
                  <span>{errors.general}</span>
                </div>
              )}

              <div>
                <label htmlFor="username" className="block text-[14px] font-medium text-white mb-0">
                  Enter Username:
                </label>
                <input
                  id="input-1"
                  name="username"
                  type="text"
                  placeholder="Enter Login Id"
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className='block w-full px-3 py-[8px] border border-gray-500 bg-gray-100 text-gray-800 placeholder-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoggingIn}
                />
                {errors.username && (
                  <p className='text-[#f43] text-sm mt-1'>{errors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-[14px] mt-[2px] font-medium text-white mb-0">
                  Enter Password:
                </label>
                <div className="relative">
                  <input
                    id="input-2"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter Password"
                    autoComplete="new-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    className='block w-full px-3 py-[8px] pr-10 border border-gray-500 bg-gray-100 text-gray-800 placeholder-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoggingIn}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoggingIn}
                  >
                    {showPassword ? (
                      <i className="fas fa-eye-slash text-gray-600"></i>
                    ) : (
                      <i className="fas fa-eye text-gray-600"></i>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className='text-[#f43] text-sm mt-1'>{errors.password}</p>
                )}
              </div>

              <button
                type='submit'
                onClick={handleSubmit}
                disabled={isLoggingIn}
                className={`w-full py-[5px] px-4 bg-[#333d79ff] text-white font-medium text-[14px] transition-colors duration-200 ${isLoggingIn ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
               LOGIN
              </button>
            </div>

            <div className="mt-6 text-[12px] text-left text-white pr-5">
              <p>This site is protected by reCAPTCHA and the Google <span className="text-[#4a7a8b]">Privacy Policy</span> and <span className="text-[#4a7a8b]">Terms of Service</span> apply.</p>
            </div>

            <div className="mt-4 text-center">
              <p className="text-white text-sm mb-2">Best Viewed In:</p>
              <div className="flex justify-center space-x-1">
                <img src={Chrome} alt="Chrome" className="w-[35px] h-[35px] object-contain" />
                <img src={Firefox} alt="Firefox" className="w-[35px] h-[35px] object-contain" />
                <img src={Edge} alt="Edge" className="w-[35px] h-[35px] object-contain" />
              </div>
            </div>
          </div>
        </div>

        <FooterSection/>
      </div>
    </>
  )
}

export default Login